import { Table, Box, HStack, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { TreatmentType } from "@keita/lanepatrol-sdk";
import { getTreatmentLabel } from "../../../../../project/utils/treatment-labels";
import { useStores } from "../../../../../shared/stores/RootStore";

const i18prefix = "map:dashboard.TableTreatmentSuggestionCounter";

type Row = {
  type: TreatmentType;
  count: number;
  avgPctAllProject: number;
  avgPctAffected: number;
};

type SortKey = keyof Omit<Row, "type">;
type SortDir = "asc" | "desc";

const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => (
  <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3 }}>
    {active && dir === "asc" ? "▲" : "▼"}
  </span>
);

const TableTreatmentSuggestionCounterBase = () => {
  const { filterStore } = useStores();
  const nodes = filterStore.filteredNodes;

  const [sortKey, setSortKey] = useState<SortKey>("count");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const data = useMemo<Row[]>(() => {
    if (!nodes.length) return [];

    const counter = new Map<TreatmentType, number>();
    const sumPct = new Map<TreatmentType, number>();

    for (const node of nodes) {
      const nodeScore = node.score?.global.value ?? 0;
      for (const treatment of node.treatments ?? []) {
        const type = treatment.type as TreatmentType;
        const impact =
          nodeScore === 0 ? 0 : nodeScore - treatment.score.global.value;

        if (impact <= 0) continue;
        if (impact <= 0) continue;
        const pct = nodeScore > 0 ? (impact / nodeScore) * 100 : 0;
        counter.set(type, (counter.get(type) ?? 0) + 1);
        sumPct.set(type, (sumPct.get(type) ?? 0) + pct);
      }
    }

    return [...counter.entries()].map(([type, count]) => {
      const totalPct = sumPct.get(type) ?? 0;
      return {
        type,
        count,
        avgPctAllProject: totalPct / nodes.length,
        avgPctAffected: totalPct / count,
      };
    });
  }, [nodes]);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) =>
      sortDir === "desc" ? b[sortKey] - a[sortKey] : a[sortKey] - b[sortKey],
    );
  }, [data, sortKey, sortDir]);

  const cols: { key: SortKey; label: string }[] = [
    { key: "count", label: t(`${i18prefix}.header.counter`) },
    {
      key: "avgPctAffected",
      label: "Avg impact reduction for nodes with suggestions",
    },
    {
      key: "avgPctAllProject",
      label: "Avg impact reduction for all nodes",
    },
  ];

  return (
    <Box py={2}>
      <Table.Root striped size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader pl={4}>
              {t(`${i18prefix}.header.treatment`)}
            </Table.ColumnHeader>
            {cols.map(({ key, label }) => (
              <Table.ColumnHeader
                key={key}
                pr={4}
                cursor="pointer"
                userSelect="none"
                onClick={() => handleSort(key)}
              >
                <HStack justify="flex-end" w="100%">
                  <Text maxW={"200px"}>{label}</Text>
                  <SortIcon active={sortKey === key} dir={sortDir} />
                </HStack>
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedData.map((r) => (
            <Table.Row key={r.type}>
              <Table.Cell pl={4}>{getTreatmentLabel(r.type)}</Table.Cell>
              <Table.Cell pr={4} textAlign="end">
                {r.count}
              </Table.Cell>
              <Table.Cell pr={4} textAlign="end">
                {r.avgPctAffected.toFixed(2)}%
              </Table.Cell>
              <Table.Cell pr={4} textAlign="end">
                {r.avgPctAllProject.toFixed(2)}%
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const TableTreatmentSuggestionCounter = observer(
  TableTreatmentSuggestionCounterBase,
);
